import { useParams, Link } from 'react-router-dom';
import blogData from '../../data/blogs.json';
import './BlogDetail.css';
import { Helmet } from 'react-helmet';

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // remove punctuation like ?
    .trim()
    .replace(/\s+/g, '-');

const BlogDetail = () => {
  const { title } = useParams();
  const { blogs } = blogData;

  const blog = blogs.find((blog) => slugify(blog.title) === title);


  if (!blog) {
    return (
      <div className="blog-detail-container">

        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Blog Post Not Found</h1>
          <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link
            to="/blog"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Enhanced content formatting with ul/ol grouping, subheaders, Q&A and TOC
  const formatContent = (content) => {
    const lines = content.split('\n').filter(line => line.trim() !== '');
    const elements = [];
    const toc = [];
    let listBuffer = [];
    let isOrdered = false;

    const slugify = (text) =>
      text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');

    const flushList = () => {
      if (listBuffer.length > 0) {
        elements.push(
          isOrdered ? (
            <ol key={`list-${elements.length}`} className="blog-content-list">
              {listBuffer}
            </ol>
          ) : (
            <ul key={`list-${elements.length}`} className="blog-content-list">
              {listBuffer}
            </ul>
          )
        );
        listBuffer = [];
      }
    };

    // helper: turn plain text with URLs into JSX with <a>
    const renderWithLinks = (text, key) => {
      // Match either inline <a ...>...</a> OR plain URLs
      const anchorTagRegex = /<a\s+href=['"]([^'"]+)['"]\s*(?:target=['"]?([^'"]*)['"]?)?\s*>(.*?)<\/a>/gi;
      const urlRegex = /(https?:\/\/[^\s]+)/g;

      let result = [];
      let lastIndex = 0;
      let match;

      while ((match = anchorTagRegex.exec(text)) !== null) {
        // push text before link
        if (match.index > lastIndex) {
          result.push(text.substring(lastIndex, match.index));
        }

        const href = match[1];
        const target = match[2] || "_blank";
        const label = match[3];

        result.push(
          <a
            key={`${key}-a-${result.length}`}
            href={href}
            target={target}
            rel="noopener noreferrer"
            className="blog-content-link"
          >
            {label}
          </a>
        );

        lastIndex = anchorTagRegex.lastIndex;
      }

      // remaining text after last link
      if (lastIndex < text.length) {
        const remaining = text.substring(lastIndex);
        const parts = remaining.split(urlRegex);
        result.push(
          ...parts.map((part, i) =>
            urlRegex.test(part) ? (
              <a
                key={`${key}-url-${i}`}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-content-link"
              >
                {part}
              </a>
            ) : (
              part
            )
          )
        );
      }

      return <span key={key}>{result}</span>;
    };

    // Helper for bullet point highlighting before colon
    const renderBulletWithHighlight = (text, key) => {
      // Find the first colon
      const colonIdx = text.indexOf(':');
      if (colonIdx !== -1) {
        const beforeColon = text.slice(0, colonIdx);
        const afterColon = text.slice(colonIdx + 1);
        return (
          <span key={key}>
            <strong>{renderWithLinks(beforeColon, `${key}-bold`)}</strong>
            {':'}
            {afterColon && (
              <span>
                {renderWithLinks(afterColon, `${key}-rest`)}
              </span>
            )}
          </span>
        );
      } else {
        // No colon, just render as normal
        return renderWithLinks(text, key);
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Blockquote
      if (/^>\s+/.test(trimmed)) {
        flushList();
        const quote = trimmed.replace(/^>\s+/, '');
        elements.push(
          <blockquote key={`q-${index}`} className="blog-quote animate-fade">
            {renderWithLinks(quote, `q-${index}`)}
          </blockquote>
        );
        return;
      }

      // Question header: lines starting with Q/Question/numbered and ending with ? optionally
      if (/^(q(uestion)?\s*\d+\.?\s+|\d+[\).]\s+).*/i.test(trimmed) || (/\?\s*$/.test(trimmed) && trimmed.length < 120)) {
        flushList();
        const id = slugify(trimmed.replace(/^(q(uestion)?\s*\d+\.?\s+|\d+[\).]\s+)/i, ''));
        toc.push({ id, text: trimmed, level: 3 });
        elements.push(
          <h3 id={id} key={`qh-${index}`} className="blog-question animate-fade">
            {trimmed}
          </h3>
        );
        return;
      }

      // Heading (all caps-ish or title line without punctuation)
      if (/^[A-Z][^.!?]*$/.test(trimmed)) {
        flushList();
        const id = slugify(trimmed);
        toc.push({ id, text: trimmed, level: 2 });
        elements.push(
          <h2 id={id} key={`h-${index}`} className="blog-content-heading animate-fade">
            {trimmed}
          </h2>
        );
        return;
      }

      // Subheader: lines ending with ':'
      if (/.*:\s*$/.test(trimmed)) {
        flushList();
        const text = trimmed.replace(/:\s*$/, '');
        const id = slugify(text);
        toc.push({ id, text, level: 3 });
        elements.push(
          <h3 id={id} key={`sh-${index}`} className="blog-subheading animate-fade">
            {text}
          </h3>
        );
        return;
      }

      // Bullet point
      if (/^[•\-\*]\s/.test(trimmed)) {
        isOrdered = false;
        const bulletText = trimmed.replace(/^[•\-\*]\s/, '');
        listBuffer.push(
          <li key={`li-${index}`} className="blog-content-list-item animate-fade">
            {renderBulletWithHighlight(bulletText, `li-${index}`)}
          </li>
        );
        return;
      }

      // Numbered list
      if (/^\d+\.\s/.test(trimmed)) {
        isOrdered = true;
        listBuffer.push(
          <li key={`oli-${index}`} className="blog-content-list-item animate-fade">
            {renderWithLinks(trimmed.replace(/^\d+\.\s/, ''), `oli-${index}`)}
          </li>
        );
        return;
      }

      // Normal paragraph
      flushList();
      elements.push(
        <p key={`p-${index}`} className="blog-content-paragraph animate-fade">
          {renderWithLinks(trimmed, `p-${index}`)}
        </p>
      );
    });

    flushList();
    return { elements, toc };
  };

  // Fallbacks for author and publishDate if not present in blog object
  const author = blog.author || "Offis Bay Team";
  const publishDate = blog.publishDate || blog.date || blog.publishedAt || "";

  return (
    <div className="blog-detail-container">
        <Helmet>
          <title>{blog.metaTitle || blog.title}</title>
          <meta name="description" content={blog.metaDescription || blog.preview} />
          {console.log(blog.metaDescription, 'margoob')}
          <meta property="og:title" content={blog.metaTitle || blog.title} />
          <meta property="og:description" content={blog.metaDescription || blog.preview} />
        </Helmet>
      {/* Hero Section */}
      <div className="blog-detail-hero pt-[2rem]">
        {/* Title Section - NOW ABOVE THE IMAGE */}
        <div className="blog-title-section">
          <div className="container mx-auto px-4 text-center">
            <h1 className="blog-detail-title animate-fade">
              {blog.title}
            </h1>
          </div>
        </div>

        {/* Image Section - NOW BELOW THE TITLE */}
        <div className="blog-detail-image-responsive">
          <img src={blog.bannerImage} alt={blog.title} className="blog-detail-img" />
        </div>
        <div className="blog-detail-meta-enhanced">
          <div className="meta-item">
            <span className="meta-icon">
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="7" r="4" />
                <path d="M5.5 21a7.5 7.5 0 0 1 13 0" />
              </svg>
            </span>
            <span>{author}</span>
          </div>
          <div className="meta-item">
            <span className="meta-icon">
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
            </span>
            <span>{formatDate(publishDate)}</span>
          </div>
        </div>

      </div>

      {/* Content Section */}
      <div className="blog-detail-content">
        <div className="container mx-auto px-4 py-12">
          <div className="blog-content-wrapper">
            <div className="blog-content-main">
              <div className="blog-content-body">
                {(() => {
                  const { elements, toc } = formatContent(blog.content);
                  return (
                    <>
                      {elements}
                      {blog.tags && blog.tags.length > 0 && (
                        <div className="blog-content-tags">
                          <h3>Tags</h3>
                          <div className="blog-tags-list">
                            {blog.tags.map((tag, i) => (
                              <span key={i} className="blog-tag">{tag}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Expose TOC to sidebar via render prop pattern by storing on window (lightweight) */}
                      {typeof window !== 'undefined' && (() => { window.__BLOG_TOC__ = toc; return null })()}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Sidebar */}
            <div className="blog-content-sidebar">
              <div className="sidebar-card">
                <h3>About Offis Bay</h3>
                <p>
                  Offis Bay is Chennai's premier coworking space, offering flexible workspaces
                  for professionals, startups, and businesses.
                </p>
                <Link to="/contact" className="sidebar-cta">
                  Get in Touch
                </Link>
              </div>

              <div className="sidebar-card">
                <h3>Quick Links</h3>
                <ul className="sidebar-links">
                  <li><Link to="/">Home</Link></li>
                  <li><Link to="/offis-spaces">Office Spaces</Link></li>
                  <li><Link to="/contact">Contact Us</Link></li>
                  <li><Link to="/blog"> All Blogs</Link></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Responsive image CSS */}
      <style>
        {`
          .blog-detail-image-responsive {
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 0 auto;
            padding: 0 0 0.5rem 0;
          }
          .blog-detail-img {
            width: 100%;
            max-width: 700px;
            height: auto;
            border-radius: 16px;
            object-fit: cover;
            display: block;
          }
          @media (max-width: 768px) {
            .blog-detail-img {
              max-width: 100%;
              border-radius: 10px;
            }
            .blog-detail-image-responsive {
              padding: 0;
            }
          }
        `}
      </style>
    </div>
  );
};

export default BlogDetail;
